import React, { useRef, useMemo, useState, useCallback, useLayoutEffect, useEffect } from 'react';
import { View, FlatList, KeyboardAvoidingView, } from 'react-native';
import Animated, { useSharedValue, useAnimatedScrollHandler } from 'react-native-reanimated';
import { rem } from 'design-system/values';
import { HEIGHT } from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import Text, { lineHeight } from 'design-system/Text';
import { AppView, NavigationHeader } from 'design-system';
import { SettingsItemNavigate } from 'design-system/SettingsItem';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import AnimatedSearchComponent from 'features/countryPicker/AnimatedSearchComponent';
import { useAppSelector } from 'store/hooks';
import { selectIsGBUser } from 'reducers/selectors';
import EmptyComponent from 'design-system/EmptyComponent';
import { isWeb, keyboardAvoidingViewBehavior } from '../../../constants';
import currencies from '../components/currencies';
const extraBtnHeight = HEIGHT + rem(16);
const getRegionSpecificCurrencies = (currencies, isGBUser) => {
    let orderedCurrencies;
    if (isGBUser) {
        const gbPriorities = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
        const prioritized = [];
        const remaining = [...currencies];
        gbPriorities.forEach((code) => {
            const index = remaining.findIndex((currency) => currency.code === code);
            if (index !== -1) {
                prioritized.push(remaining[index]);
                remaining.splice(index, 1);
            }
        });
        orderedCurrencies = [...prioritized, ...remaining];
    }
    else {
        orderedCurrencies = currencies;
    }
    return orderedCurrencies.map((currency, index) => ({
        name: currency.name,
        code: currency.code,
        isFirstElement: index === 0,
        isLastElement: index === orderedCurrencies.length - 1,
    }));
};
const AnimatedFlatList = Animated.createAnimatedComponent(FlatList);
const keyExtractor = (item) => item.code;
const DummyAccountImportCurrencyScreen = ({ navigation, route }) => {
    const flatListRef = useRef(null);
    const colors = useColors();
    const isGBUser = useAppSelector(selectIsGBUser);
    const scrollY = useSharedValue(0);
    const [headerInfoHeight, setHeaderInfoHeight] = useState(0);
    const [currenciesList, setCurrenciesList] = useState(() => getRegionSpecificCurrencies(currencies, isGBUser));
    // Fallback for web if text layout doesn't work
    useEffect(() => {
        if (isWeb && headerInfoHeight === 0) {
            const fallbackHeight = rem(16 + 1 * lineHeight.Text_24);
            setHeaderInfoHeight(fallbackHeight);
        }
    }, [headerInfoHeight]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            ...(isWeb && {
                title: 'What currency should this account use?',
                headerTitle: EmptyComponent,
            }),
        });
    }, [colors.text.primary]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const linesRef = useRef({});
    const updatedMeasurement = useCallback(() => {
        const measurements = linesRef.current;
        if (measurements.title !== undefined) {
            const height = rem(16 + measurements.title * lineHeight.Text_24);
            setHeaderInfoHeight(height);
        }
    }, []);
    const onTextLayoutTitle = useCallback((event) => {
        const lines = event.nativeEvent.lines.length;
        if (linesRef.current.title === lines)
            return;
        linesRef.current = {
            title: lines,
        };
        updatedMeasurement();
    }, [updatedMeasurement]);
    const onChangeSearchTerm = useCallback((data) => {
        if (data === '') {
            setCurrenciesList(getRegionSpecificCurrencies(currencies, isGBUser));
        }
        else {
            const textWord = data.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, ' ');
            const results = currencies.filter((currency) => {
                const wordsToMatch = textWord.toLowerCase().split(' ');
                return wordsToMatch.every((word) => currency.name.toLowerCase().match(word.toLowerCase()) ||
                    currency.code.toLowerCase().match(word.toLowerCase()));
            });
            setCurrenciesList(getRegionSpecificCurrencies(results, isGBUser));
        }
    }, [isGBUser]);
    const onPress = useCallback((currency) => {
        navigation.navigateDeprecated('DummyAccountCreated', {
            ...(route.params || {}),
            currency: currency.code,
            currencyName: currency.name,
        });
    }, [navigation, route.params]);
    const styles = useStyles(styleSet);
    const renderItem = useCallback(({ item }) => (<View style={[
            styles.countryRow,
            item.isLastElement && styles.lastRowItem,
            item.isFirstElement && styles.firstRowItem,
        ]}>
        <SettingsItemNavigate title={`${item.name} (${item.code})`} onPress={() => onPress({ name: item.name, code: item.code })}/>
      </View>), [onPress, styles.countryRow, styles.firstRowItem, styles.lastRowItem]);
    const ListHeaderComponent = useMemo(() => (<View style={styles.titleContainer}>
        <Text Text-24 onTextLayout={onTextLayoutTitle}>
          What currency should this account use?
        </Text>
        <View style={styles.searchResultsSpacer}/>
      </View>), [onTextLayoutTitle, styles.titleContainer, styles.searchResultsSpacer]);
    const scrollToSearchBar = useCallback(() => {
        if (scrollY.value < headerInfoHeight) {
            flatListRef.current?.scrollToOffset({
                offset: 0,
                animated: true,
            });
        }
    }, [headerInfoHeight, scrollY]);
    const headerInputRange = useMemo(() => [0, headerInfoHeight], [headerInfoHeight]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', extraBtnHeight, paddingHorizontalStyle);
    return (<AppView type="secondary" withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <KeyboardAvoidingView style={styles.flex} behavior={keyboardAvoidingViewBehavior}>
        <View style={styles.flex}>
          <NavigationHeader hasShadow={false} scrollY={scrollY} bgColorInputRange={headerInputRange} shadowInputRange={headerInputRange} withFrame/>

          <AnimatedFlatList ref={flatListRef} data={currenciesList} renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} keyExtractor={keyExtractor} keyboardShouldPersistTaps="handled" ListHeaderComponent={ListHeaderComponent} contentContainerStyle={contentContainerStyle}/>
          {!!headerInfoHeight && (<AnimatedSearchComponent scrollY={scrollY} headerInfoHeight={headerInfoHeight} scrollToSearchBar={scrollToSearchBar} onChangeSearchTerm={onChangeSearchTerm}/>)}
        </View>
      </KeyboardAvoidingView>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    separator: {
        height: 1,
        marginLeft: rem(16),
    },
    sectionContainer: {
        paddingTop: rem(24),
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
        backgroundColor: colors.background.dark,
    },
    titleContainer: {
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
    searchResultsSpacer: {
        height: rem(74),
    },
    countryRow: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
    firstRowItem: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(isWeb && {
            paddingTop: rem(8),
        }),
    },
    lastRowItem: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(isWeb && {
            paddingBottom: rem(8),
        }),
    },
}));
export default DummyAccountImportCurrencyScreen;
